/**
 * Client Request ▸ Request Registration — the full-page DETAIL (standing rule
 * 12 / BO UI Standard §4). The list is REPLACED by this page; it is never a
 * popup.
 *
 * REQUEST INFORMATION — the complete prototype v21 field set:
 *   Request ID (read-only; "Generated when saved" until minted) · Status
 *   (read-only, with its position in the §9.1 sequence) · Client * ·
 *   Client contact * · Project · Priority * · Quote ID · Owner (read-only,
 *   assigned at scheduling) · Client request description * (rich text).
 *
 * PROJECT IS OPTIONAL (owner, 2026-10-14: "not every client have the project")
 * — no *, no required tint, and it holds nothing back at the release gate.
 *
 * Client contact and Description carry the red * and the required
 * tint, but a DRAFT save does not enforce them (owner decision 2026-09-04):
 * Draft exists precisely to park a request that is not yet complete. Full
 * enforcement is the Save & Schedule release gate (§10.3) — see the disabled
 * button below.
 *
 * ACTIONS — Cancel · Audit · Save Draft · Save & Close · Save & Schedule, per
 * the prototype, rendered at TOP and BOTTOM with the identical set (BO UI §4)
 * and driven by the shared `useSaveFlow` so every save is
 * confirm → audited handler → "Saved successfully." → the chosen after-step.
 *
 * CLIENT CHANGE CASCADE (design §10.1): changing the Client clears Contact and
 * Project unless they are on file for the NEW client, and the user is TOLD what
 * was cleared — unproven parentage counts as invalid.
 *
 * Layout, control sizes, fonts and spacing come from `SettingsDetailPage.css`;
 * this page defines none of its own (standing rule 9).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../modules/SettingsDetailPage.css';
// This page's shell uses `bl-page` / `bl-grid-area`, so it declares that
// dependency itself. It had been relying on the LIST page having imported the
// file first — true in practice, since the list is always rendered before the
// detail, but a page whose styling depends on which page ran before it is one
// route change away from rendering unstyled.
import '../backlog/backlog.css';
import './clientRequest.css';
import { AuditTrailButton } from '../../components/AuditTrailButton';
import { LookupCombo, type LookupData } from '../../components/LookupCombo';
import { BusyIcon, LoaderSymbol } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { useConfirm, useSaveFlow } from '../../components/confirm';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { RichTextEditor } from '../settings/systemManagement/RichTextEditor';
import { fmtLocalDateTime } from '../../utils/datetime';
import QuickAddDialog, { type QuickAddKind } from './QuickAddDialog';
import ServiceTasksSection, {
  type FlushResult, type ServiceTasksHandle, type TaskSummary,
} from './ServiceTasksSection';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import type { StepState } from '../../components/StepIsland/StepIsland';
import {
  AUDIT_FORM_NAME, DEFAULT_PRIORITY_NAME, HEARD_FALLBACK, type PriorityOption,
  createRequest, fetchClients, fetchContacts, fetchHeardOptions, fetchOwners,
  fetchPriorities, fetchProjects, fetchRequest, releaseRequest, updateRequest,
  type ClientRequestInput, type LookupItem, type ProjectItem,
} from './clientRequestApi';
import { labels, refuseSave, type MissingField } from './refuseSave';
import { confirmDiscardIfUnsaved, useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { openRequestSummary } from './requestSummary';
import { useAuthStore } from '../../auth/store';

/** The request lifecycle (design §9.1). Status is NEVER typed — it renders
 *  read-only with its position in this sequence, and moves only through these
 *  transitions. */
const STATUS_STEPS = ['Draft', 'Submitted', 'Scheduled', 'In Progress', 'Completed'];

interface Props {
  /** null = registering a new request; otherwise the request being edited. */
  requestId:  string | null;
  /** The ids of the list page the user came from, so the prev/next arrows walk
   *  exactly what they were looking at. */
  siblingIds: string[];
  /** Return to the list (Cancel / Save & Close). */
  onClose:    () => void;
  /** Jump to another record without going back through the list. */
  onNavigate: (id: string) => void;
}

/** The editable shape of the form. Ids are '' rather than null so every control
 *  is a controlled component from the first render. */
interface FormState {
  clientId:    string;
  contactId:   string;
  projectId:   string;
  /** The chosen Priority row's Oid (Settings → Utility → Client Request →
   *  Priority); '' = none chosen. */
  priorityId:  string;
  quoteNo:     string;
  /** `YYYY-MM-DD`, the native date input's own format.  Kept as a STRING all
   *  the way to the wire: routing a calendar day through a `Date` is how a
   *  "called on the 9th" becomes the 8th for anyone west of the server. */
  calledOn:    string;
  heardAbout:  string;
  /** The lab user accountable for the request. Editable from v68; '' = nobody
   *  chosen yet, which is a legitimate state for a draft. */
  ownerId:     string;
  description: string;   // HTML — the description is rich text
}

/** Today as `YYYY-MM-DD`, built from LOCAL date parts.  Not `toISOString()`
 *  (that is UTC — wrong day for half the world's evening) and not
 *  `toLocaleDateString` (banned in components, §18.15 C2). */
function todayLocalIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

const EMPTY: FormState = {
  clientId: '', contactId: '', projectId: '',
  // Next Day is the lab's ordinary commitment; the two faster options are a
  // deliberate choice the person taking the call makes. Filled by the effect
  // below once the Priority rows arrive (it needs the row's Oid).
  priorityId: '', quoteNo: '',
  // A new request defaults to today, because the overwhelmingly common case is
  // logging a call as it happens — but it stays editable for the one that is
  // written up the next morning.
  calledOn: todayLocalIso(), heardAbout: '',
  // Filled with the signed-in user by the effect below: the person taking the
  // call owns it until they say otherwise.
  ownerId: '',
  description: '',
};

/**
 * LookupCombo's grid popup returns column 0 as the value, so the label is
 * column 0 — and, as of 2026-09-09, the ONLY column.
 *
 * <p>The pickers used to carry a second "detail" column, which earned its place
 * on none of the three: Client showed `Customer.CustomerId`, which is blank for
 * every row on this database; Project showed `ProjectName` beside a label that
 * is `ProjectId`, and the two hold the same text here, so it repeated itself;
 * Contact showed the email, which is real but was not what the picker is for.
 * A column that is empty or duplicated is noise in a list someone is scanning
 * to pick one row.</p>
 *
 * <p>The API still returns `secondary`, so restoring a column for one picker is
 * a one-line change if a lab ever populates those fields.</p>
 */
function toLookupData(items: LookupItem[]): LookupData {
  return {
    cols: ['Name'],
    rows: items.map(i => [i.label]),
  };
}

/** One line of the Review and release checklist. It always states WHY, not just
 *  pass or fail: a checklist the user cannot act on is a slower "no". */
function CheckLine({ ok, label, detail }: { ok: boolean; label: string; detail: string }) {
  return (
    <div className={`cr-ckl ${ok ? 'ok' : 'no'}`}>
      <i className={`ti ${ok ? 'ti-circle-check' : 'ti-alert-circle'}`} />
      <div>
        <div className="cr-ckl-k">{label}</div>
        <div className="cr-ckl-v">{detail}</div>
      </div>
    </div>
  );
}

/** Rich text is empty when it has no TEXT, whatever markup wraps it — an
 *  editor left untouched still holds "<p><br></p>", which is not content. */
function htmlIsBlank(html: string): boolean {
  return !html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

export default function ClientRequestDetailPage({ requestId, siblingIds, onClose, onNavigate }: Props) {
  const [form,      setForm]      = useState<FormState>(EMPTY);
  const [initial,   setInitial]   = useState<FormState>(EMPTY);
  const [loading,   setLoading]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [triedSave, setTriedSave] = useState(false);
  const [error,     setError]     = useState<string>();

  // Read-only context for a saved record.
  const [requestNo, setRequestNo] = useState<string | null>(null);
  const [status,    setStatus]    = useState<string>('Draft');
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [ownerName, setOwnerName] = useState<string | null>(null);

  /** Which wizard step the user is on, and the real service state behind it.
   *  The services live in ServiceTasksSection, which reports up — the page
   *  must never guess at them (§10.3/§10.4). */
  const [wiz,   setWiz]   = useState(0);
  const [tasks, setTasks] = useState<TaskSummary>({ total: 0, problems: [], unsaved: 0 });

  /** The services section, so every Save on this page can commit a service the
   *  user described but did not press Add Service on (owner request
   *  2026-10-14). The section owns those drafts; only it can write them. */
  const services = useRef<ServiceTasksHandle>(null);

  /** The Request ID minted during THIS click, before the record has reloaded
   *  and `requestNo` holds it — so a message can name the request it just
   *  created (owner request 2026-10-14). */
  const mintedNoRef = useRef<string | null>(null);
  /** The id `ensureRequestId` just minted. Switching onto it reloads the record
   *  QUIETLY: the loading state would unmount the Service tasks section while the
   *  service that minted the request is still being saved, and the remounted
   *  section would list the request before that service exists (E2E 2026-10-18:
   *  "No service tasks yet" after the first save until the request was reopened). */
  const mintedIdRef = useRef<string | null>(null);

  const [priorities, setPriorities] = useState<PriorityOption[]>([]);
  /** The saved request's priority NAME — shown if that row has since been
   *  deleted in Settings and so is no longer offered. */
  const [savedPriorityName, setSavedPriorityName] = useState<string | null>(null);
  const [heardOptions, setHeardOptions] = useState<string[]>(HEARD_FALLBACK);
  const [owners,     setOwners]     = useState<LookupItem[]>([]);
  const [clients,    setClients]    = useState<LookupItem[]>([]);
  const [contacts,   setContacts]   = useState<LookupItem[]>([]);
  const [projects,   setProjects]   = useState<ProjectItem[]>([]);
  const [quickAdd,   setQuickAdd]   = useState<QuickAddKind | null>(null);

  const notify   = useNotify();

  /** Who is signed in — a new request defaults to them as Owner (v68). */

  const authUser = useAuthStore(st => st.user);
  const saveFlow = useSaveFlow();
  const { alert, confirm } = useConfirm();

  /** Tells the user which service was left behind, and why, after a save that
   *  wrote everything it could. A warning routes to the shared modal (§18.6),
   *  so it cannot scroll away unseen like the work it is reporting on did. */
  const reportBlocked = useCallback(async (f: FlushResult | null | undefined) => {
    if (!f?.blocked.length) return;
    await alert({
      title: f.blocked.length > 1 ? 'Some services were not saved' : 'One service was not saved',
      tone: 'warning',
      okLabel: 'Got it',
      message: 'The request is saved. These services still need an answer before '
             + 'they can be, and are still on screen:\n\n'
             + f.blocked.map(b => `• ${b}`).join('\n'),
    });
  }, [alert]);

  const isNew = requestId === null;

  // ── Load: priorities + clients once, the record when editing ──────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [p, h, c, o] = await Promise.all([
          fetchPriorities(), fetchHeardOptions(), fetchClients(), fetchOwners(),
        ]);
        if (!alive) return;
        setPriorities(p);
        setHeardOptions(h.length ? h : HEARD_FALLBACK);
        setClients(c);
        setOwners(o);
      } catch {
        if (alive) notify.warning('Could not load the client list. Try Refresh, or reopen the form.');
      }
    })();
    return () => { alive = false; };
  }, [notify]);

  /* A NEW request is owned by whoever is taking the call, so Owner defaults to
     the signed-in user. Matched on the user's own id first and their name only
     as a fallback: the roster is dbo.Employee and the JWT carries the same Oid,
     but a lab whose two directories have drifted should still land on the right
     person rather than silently on nobody. Only ever fills a BLANK owner, so it
     can never overwrite a choice the user made or a saved record's value. */
  useEffect(() => {
    if (requestId !== null || owners.length === 0) return;
    setForm(f => {
      if (f.ownerId) return f;
      const me = owners.find(o => o.id === authUser?.id)
              ?? owners.find(o => (o.label || '').trim() === (authUser?.name || '').trim());
      return me ? { ...f, ownerId: me.id } : f;
    });
  }, [requestId, owners, authUser]);

  /* A NEW request starts on Next Day, as it always has — now as that row's Oid.
     Written to the baseline too, so a default the user never touched does not
     count as an unsaved change. Only ever fills a BLANK priority. */
  useEffect(() => {
    if (requestId !== null || priorities.length === 0) return;
    const def = priorities.find(p => p.label === DEFAULT_PRIORITY_NAME)?.id;
    if (!def) return;
    setForm(f => f.priorityId ? f : { ...f, priorityId: def });
    setInitial(f => f.priorityId ? f : { ...f, priorityId: def });
  }, [requestId, priorities]);

  useEffect(() => {
    if (requestId === null) {
      setForm(EMPTY); setInitial(EMPTY); setSavedPriorityName(null);
      setRequestNo(null); setStatus('Draft'); setCreatedAt(null); setOwnerName(null);
      setTriedSave(false); setError(undefined);
      return;
    }
    let alive = true;
    const quiet = mintedIdRef.current === requestId;
    mintedIdRef.current = null;
    if (!quiet) setLoading(true);
    setError(undefined);
    (async () => {
      try {
        const r = await fetchRequest(requestId);
        if (!alive) return;
        if (!r) { setError('This request no longer exists.'); return; }
        const next: FormState = {
          clientId:    r.clientId ?? '',
          contactId:   r.contactId ?? '',
          projectId:   r.projectId ?? '',
          priorityId:  r.priorityId ?? '',
          quoteNo:     r.quoteNo ?? '',
          // Slice, don't parse: the wire value is a naive date-time for a DATE
          // column, and `new Date(...)` on it would re-introduce the shift.
          calledOn:    (r.calledOn ?? '').slice(0, 10),
          heardAbout:  r.heardAbout ?? '',
          ownerId:     r.ownerId ?? '',
          description: r.description ?? '',
        };
        setForm(next); setInitial(next); setSavedPriorityName(r.priority ?? null);
        setRequestNo(r.requestNo);
        setStatus(r.status ?? 'Draft');
        setCreatedAt(r.createdAt);
        setOwnerName(r.ownerName);
        setTriedSave(false);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : 'Failed to load the request.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [requestId]);

  // Contacts and Projects are NARROWED to the selected client (§10.1), and
  // cleared with it so a stale list can never be picked from.
  const reloadNarrowed = useCallback(async (clientId: string) => {
    if (!clientId) { setContacts([]); setProjects([]); return; }
    try {
      const [ct, pj] = await Promise.all([fetchContacts(clientId), fetchProjects(clientId)]);
      setContacts(ct); setProjects(pj);
    } catch { setContacts([]); setProjects([]); }
  }, []);

  useEffect(() => { void reloadNarrowed(form.clientId); }, [form.clientId, reloadNarrowed]);

  // ── Client change cascade (§10.1) ─────────────────────────────────────────
  const onClientPick = useCallback(async (row: string[]) => {
    const label = row[0] ?? '';
    const nextClientId = clients.find(c => c.label === label)?.id ?? '';
    if (nextClientId === form.clientId) return;

    if (!form.contactId && !form.projectId) {
      setForm(f => ({ ...f, clientId: nextClientId }));
      return;
    }

    // Ask the NEW client what it has on file and keep only what survives.
    // Unproven parentage counts as invalid: a failed lookup clears rather than
    // keeping a value we cannot vouch for.
    let keptContact = '';
    let keptProject = '';
    if (nextClientId) {
      try {
        const [ct, pj] = await Promise.all([
          fetchContacts(nextClientId), fetchProjects(nextClientId),
        ]);
        if (form.contactId && ct.some(c => c.id === form.contactId)) keptContact = form.contactId;
        if (form.projectId && pj.some(p => p.id === form.projectId)) keptProject = form.projectId;
      } catch { /* keep nothing — see above */ }
    }

    const cleared: string[] = [];
    if (form.contactId && !keptContact) cleared.push('Client contact');
    if (form.projectId && !keptProject) cleared.push('Project');

    setForm(f => ({ ...f, clientId: nextClientId, contactId: keptContact, projectId: keptProject }));
    if (cleared.length) {
      // NAMED, not "the selected client": the user has just changed it, so the
      // one thing they need to check is which client it is now talking about.
      const who = clients.find(c => c.id === nextClientId)?.label;
      notify.info(`${cleared.join(' and ')} cleared — not on file for `
                + `${who ? who : 'the selected client'}.`);
    }
  }, [clients, form.clientId, form.contactId, form.projectId, notify]);

  /** A quick-added record is selected immediately, and its list refreshed so the
   *  new row is really there behind the selection.
   *
   *  A new CLIENT also CLEARS Contact and Project: those belonged to whichever
   *  client was selected before, and §10.1 narrows both to the current one — so
   *  keeping them would leave the request carrying a contact that is not on file
   *  for the client it now names. The freshly created client has neither yet,
   *  which is exactly what the two ＋ buttons beside them are for. */
  const onQuickAdded = useCallback(async (kind: QuickAddKind, id: string) => {
    if (kind === 'client') {
      setClients(await fetchClients());
      setForm(f => ({ ...f, clientId: id, contactId: '', projectId: '' }));
      await reloadNarrowed(id);
      return;
    }
    await reloadNarrowed(form.clientId);
    setForm(f => kind === 'contact' ? { ...f, contactId: id } : { ...f, projectId: id });
  }, [form.clientId, reloadNarrowed]);

  // ── Derived ───────────────────────────────────────────────────────────────
  // Each picker shows ONE row per distinct display value (2026-09-04). The
  // Project picker's display value is the CODE, so its second column carries the
  // project name rather than the other way round.
  const clientLk  = useMemo(() => toLookupData(clients),  [clients]);
  const contactLk = useMemo(() => toLookupData(contacts), [contacts]);
  const projectLk = useMemo(() => toLookupData(projects), [projects]);

  const clientLabel  = clients.find(c => c.id === form.clientId)?.label ?? '';
  const contactLabel = contacts.find(c => c.id === form.contactId)?.label ?? '';
  const projectLabel = projects.find(p => p.id === form.projectId)?.label ?? '';
  /** The chosen project's site address — where a new service's Where starts. */
  const projectSite  = projects.find(p => p.id === form.projectId)?.location ?? null;
  /** The chosen priority's NAME — what the summary prints and what a new
   *  service's Turnaround is matched to. */
  const priorityLabel = priorities.find(p => p.id === form.priorityId)?.label
                      ?? (form.priorityId ? savedPriorityName ?? '' : '');

  const recordName = isNew ? 'New request' : (requestNo ?? '…');
  const dirty = useMemo(
    () => (Object.keys(form) as (keyof FormState)[]).some(k => form[k] !== initial[k]),
    [form, initial]);

  const idx    = requestId ? siblingIds.indexOf(requestId) : -1;
  const prevId = idx > 0 ? siblingIds[idx - 1] : null;
  const nextId = idx >= 0 && idx < siblingIds.length - 1 ? siblingIds[idx + 1] : null;

  /** Position in the §9.1 sequence, e.g. "Step 1 of 5 · next: Submitted".
   *  Blank for a status outside the sequence, so an unexpected value degrades
   *  to just the pill rather than to "Step 0 of 5". */
  const statusStep = useMemo(() => {
    const i = STATUS_STEPS.indexOf(status);
    if (i < 0) return '';
    const next = STATUS_STEPS[i + 1];
    return `Step ${i + 1} of ${STATUS_STEPS.length}${next ? ` · next: ${next}` : ''}`;
  }, [status]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm(f => ({ ...f, [k]: v }));

  /* UNSAVED CHANGES (standing rule 15). The automatic tracker sees native
     inputs, but this form's real content lives in custom widgets — the three
     LookupCombos and the rich-text description — so it states its own dirty
     state explicitly, which is also exact rather than heuristic. Every in-page
     exit (Cancel, breadcrumb, prev/next) and every cross-page navigation then
     asks before the work is lost. */
  useUnsavedGuard(dirty);
  const guardedClose = useCallback(async () => {
    if (await confirmDiscardIfUnsaved()) onClose();
  }, [onClose]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const payload = (): ClientRequestInput => ({
    clientId:    form.clientId || null,
    contactId:   form.contactId || null,
    projectId:   form.projectId || null,
    // The server resolves the name from the Id and stores both.
    priority:    null,
    priorityId:  form.priorityId || null,
    quoteNo:     form.quoteNo.trim() || null,
    calledOn:    form.calledOn || null,
    heardAbout:  form.heardAbout || null,
    description: htmlIsBlank(form.description) ? null : form.description,
    ownerId:     form.ownerId || null,
  });

  /** after: what happens once the save commits — the ONLY difference between
   *  Save Draft and Save & Close. */
  const doSave = useCallback(async (after: 'stay' | 'close') => {
    setTriedSave(true);

    // Client is the one hard requirement for a Draft. Contact / Project /
    // Description show their * and tint, but a Draft is allowed to be
    // incomplete — that is what Draft is for. The release gate enforces them.
    if (!form.clientId) {
      await refuseSave([{ label: 'Client', anchorId: 'cr-f-client' }], { subject: 'request' });
      return;
    }

    // THE SERVICE'S OWN REQUIRED ANSWERS, CHECKED HERE (owner request
    // 2026-10-14: "if the service need any mandatory data, display the message
    // while save the registration"). The services are saved WITH the request
    // now, so saving the registration is the moment the user finds out — and it
    // has to come BEFORE the write, not after: the server refuses an incomplete
    // service (§14b.5), so a save that went ahead would store the request and
    // leave the service behind, which is the data loss this whole flow exists
    // to stop. Nothing is written, so the user fixes the field and saves again.
    const serviceMissing = services.current?.checkPending() ?? [];
    if (serviceMissing.length) {
      await refuseSave(serviceMissing, {
        subject: 'service',
        title: `Not saved — the service needs ${serviceMissing.length} more `
             + `answer${serviceMissing.length === 1 ? '' : 's'}`,
        message: 'A service on this request is saved together with it, and cannot '
               + 'be saved yet. Fill in the following and save again:',
      });
      return;
    }

    let mintedNo: string | null = null;
    let flushed: FlushResult | null = null;
    setSaving(true);
    try {
      await saveFlow({
        // Create is reason-exempt; an edit gates the reason when Settings →
        // Audit Trail Configuration has ReasonRequired ON for this form.
        formName: AUDIT_FORM_NAME,
        auditAction: isNew ? 'create' : 'edit',
        save: async () => {
          let id = requestId;
          if (isNew) {
            const created = await createRequest(payload());
            mintedNo = created.requestNo;
            id = created.id;
          } else {
            await updateRequest(requestId!, payload());
          }

          // THE SERVICES GO WITH THE REQUEST (owner request 2026-10-14). A
          // service described in the editor but never committed with Add
          // Service used to be discarded by this save; now the same click
          // writes it. It runs AFTER the request exists — a child cannot be
          // saved before its parent (§4.3) — and BEFORE the navigation below,
          // so a service this save cannot write is still on screen afterwards
          // rather than lost to a remount.
          if (services.current?.hasPending()) flushed = await services.current.flush(id!);

          // Staying on a brand-new record must continue editing THAT record,
          // not silently start a second one on the next save. A blocked service
          // keeps the user here even on Save & Close, so that counts as staying
          // — without this the page would still think it holds a new record and
          // the next save would create a SECOND request.
          if (isNew && (after === 'stay' || flushed?.blocked.length)) onNavigate(id!);
        },
        // A FUNCTION, not a string: the ID does not exist until `save` has run,
        // so a string here would always be the pre-save value — which is why
        // the minted RequestNo never reached the acknowledgement. (§18.6 names
        // this one of the few flows allowed to echo a new ID.)
        successMessage: () => (mintedNo ? `Request ${mintedNo} saved successfully.` : undefined),
        onSaved: async () => {
          // A service that could not be written keeps the user HERE, whichever
          // button they pressed: closing the page is what would destroy it, and
          // the warning names the fields it needs so the fix is one step away.
          if (flushed?.blocked.length) { await reportBlocked(flushed); setInitial(form); return; }
          if (after === 'close') onClose();
          else setInitial(form);
        },
      });
    } finally {
      setSaving(false);
    }
  }, [form, isNew, requestId, saveFlow, notify, onClose, onNavigate, reportBlocked]);

  /**
   * A CHILD CANNOT EXIST BEFORE ITS PARENT (design §4.3).
   *
   * Adding a Service Task to a request that has never been saved must mint the
   * REQUEST first — otherwise the service's audit rows carry a blank Source,
   * which never re-attaches once the Request ID is finally minted.
   *
   * AND IT MUST BE THE REQUEST THE USER IS LOOKING AT. An existing record that
   * has unsaved edits is persisted here too — otherwise everything downstream
   * works from the version last written rather than the one on screen: the
   * server re-checks the release gate against the STORED row, so the user can be
   * refused for a description they can see themselves having typed, and a Save &
   * Schedule would release a request whose reviewed changes were then discarded
   * on the reload. (No reason prompt on this path: the caller has already
   * confirmed the action it is about to take, and the property diffs are audited
   * server-side either way.)
   *
   * Returns the request id, minting it if this is still a new record; null when
   * the request is not complete enough to save, in which case the caller stops
   * and the user is told exactly what is missing rather than getting a silent
   * failure.
   */
  const ensureRequestId = useCallback(async (
    { silent = false }: { silent?: boolean } = {},
  ): Promise<string | null> => {
    if (requestId) {
      if (!dirty) return requestId;
      setSaving(true);
      try {
        await updateRequest(requestId, payload());
        setInitial(form);
        return requestId;
      } catch (e) {
        notify.error(e instanceof Error
          ? `Could not save the request: ${e.message}`
          : 'Could not save the request.');
        return null;
      } finally {
        setSaving(false);
      }
    }

    setTriedSave(true);
    if (!form.clientId) {
      await refuseSave([{ label: 'Client', anchorId: 'cr-f-client' }], {
        subject: 'request',
        title: 'Not saved — a service needs its request first',
        message: 'A service hangs off a saved request, so the request has to be '
               + 'created before one can be added. Fill this in and try again:',
      });
      return null;
    }

    setSaving(true);
    try {
      const created = await createRequest(payload());
      // Remembered because the caller may want to NAME the request it has just
      // minted, and `requestNo` state does not hold it until the record reloads
      // — Save & Schedule says "Request RR… released for scheduling." (owner
      // request 2026-10-14).
      mintedNoRef.current = created.requestNo;
      mintedIdRef.current = created.id;
      // The release path says it ONCE, at the end, with the ID in it: announcing
      // the save here as well would put up a banner that its own next message
      // replaces a moment later, for the same click.
      if (!silent) notify.success(`Request ${created.requestNo} saved successfully.`);
      // Switch the page onto the real record so everything below it — the
      // Service tasks list, the audit chip, the prev/next arrows — is now
      // working against a persisted request rather than a draft.
      onNavigate(created.id);
      return created.id;
    } catch (e) {
      notify.error(e instanceof Error
        ? `Could not save the request: ${e.message}`
        : 'Could not save the request.');
      return null;
    } finally {
      setSaving(false);
    }
  }, [requestId, dirty, form, notify, onNavigate]);

  const missing = (v: string) => triedSave && !v;

  /** What the §10.3 release gate would still be waiting on. Shown as the Save &
   *  Schedule tooltip so the disabled button explains itself rather than just
   *  being inert. */
  /** What the Request information section is still missing — the first wizard
   *  step's gate, and the first line of the release gate. */
  /** What §10.1 still wants, each entry carrying the row it lives on so a
   *  refusal can jump straight to it (see refuseSave). */
  const requestMissing = useMemo<MissingField[]>(() => {
    const need: [MissingField, boolean][] = [
      [{ label: 'Client',                     anchorId: 'cr-f-client' },   !form.clientId],
      [{ label: 'Client contact',             anchorId: 'cr-f-contact' },  !form.contactId],
      // Project is NOT here (owner, 2026-10-14): it is an optional field, so it
      // cannot hold a request back from scheduling. The server's own gate
      // (ClientRequestController.ReleaseBlockersAsync) was relaxed to match —
      // the two must agree or the user is refused for something this list says
      // is fine.
      [{ label: 'Priority',                   anchorId: 'cr-f-priority' }, !form.priorityId],
      [{ label: 'Client request description', anchorId: 'cr-f-desc' },
       htmlIsBlank(form.description)],
    ];
    return need.filter(([, bad]) => bad).map(([f]) => f);
  }, [form]);

  /** Draft is the only unreleased status (§9.1) — everything after it means the
   *  request has left the desk. */
  const released = !!status && status !== 'Draft';

  const [releasing, setReleasing] = useState(false);

  const releaseBlockers = useMemo<MissingField[]>(() => {
    const b: MissingField[] = [...requestMissing];
    // Real service state, reported up by the section that owns it.  These are
    // deliberately anchor-less: "SV…: Where, When" is not one row on this page.
    if (tasks.total === 0) b.push({ label: 'At least one Service Task — none added' });
    else b.push(...tasks.problems.map(label => ({ label })));
    return b;
  }, [requestMissing, tasks]);

  /**
   * Save & Schedule — the §10.3 release gate.
   *
   * It SAVES FIRST, deliberately: the checklist is computed from the form as it
   * stands on screen, so releasing without persisting would submit a request
   * whose stored version is not the one the user just reviewed.
   *
   * The server re-checks the whole gate and returns its own blocker list; that
   * list is what gets shown, not the client's, so the user is never told a
   * different story by the two.
   */
  const doRelease = useCallback(async () => {
    // The service's own required answers come first, before anything is written
    // — the same message the other two Saves give, for the same reason.
    const serviceMissing = services.current?.checkPending() ?? [];
    if (serviceMissing.length) {
      await refuseSave(serviceMissing, {
        subject: 'service',
        title: 'Not ready to schedule — the service is incomplete',
        message: 'A service on this request is saved together with it, and cannot '
               + 'be saved yet. Fill in the following and try again:',
      });
      return;
    }

    if (releaseBlockers.length) {
      await refuseSave(releaseBlockers, {
        subject: 'request',
        title: 'Not ready to schedule',
        message: 'The request has not been released. It still needs:',
      });
      return;
    }

    const ok = await confirm({
      title: 'Save & Schedule',
      message: 'This releases the request for scheduling and it becomes visible to '
             + 'dispatch. Do you want to continue?',
    });
    if (!ok) return;

    setReleasing(true);
    try {
      // `silent`: this path announces itself ONCE at the end, naming the request.
      const id = await ensureRequestId({ silent: true });
      if (!id) return;                    // save failed; the user already knows why

      // The services go with it (owner request 2026-10-14). AFTER the confirm,
      // deliberately: `ensureRequestId` announces a newly minted request, and
      // saving before the dialog put that banner on screen UNDERNEATH the
      // still-open "Do you want to continue?" — two messages at once (owner
      // report 2026-10-14). Their required answers were checked at the top, so
      // anything blocked here is the SERVER refusing, not a validation.
      if (services.current?.hasPending()) {
        const flushed = await services.current.flush(id);
        if (flushed.blocked.length) { await reportBlocked(flushed); return; }
      }

      const next = await releaseRequest(id);
      setStatus(next);
      // NAME THE REQUEST (owner request 2026-10-14). Going straight to Save &
      // Schedule on a new request mints its ID in this very click, and the plain
      // "Released for scheduling." never said what it was — the one thing the
      // user needs to quote afterwards. The LOADED number wins: `mintedNoRef`
      // belongs to whichever request this page last minted, so preferring it
      // would name a stale one after the prev/next arrows moved to another
      // record. It is only consulted when there is no loaded number — which is
      // exactly the new-request case, where it is the one that was just minted.
      const no = requestNo ?? mintedNoRef.current;
      notify.success(no ? `Request ${no} released for scheduling.`
                        : 'Released for scheduling.');
      onNavigate(id);
    } catch (e) {
      // A server-side blocker list is the useful answer, not a one-line failure.
      const details = (e as { details?: string[] })?.details;
      if (Array.isArray(details) && details.length) {
        await alert({
          title: 'Not ready to schedule',
          message: details.join('\n'),
          tone: 'warning',
          okLabel: 'Got it',
        });
      } else {
        notify.error(e instanceof Error ? `Could not release the request: ${e.message}`
                                        : 'Could not release the request.');
      }
    } finally {
      setReleasing(false);
    }
  }, [releaseBlockers, requestNo, alert, confirm, ensureRequestId, notify, onNavigate, reportBlocked]);

  /* ── The wizard (§10.4) ───────────────────────────────────────────────────
     Four steps, and how far the record has actually got. A step is LOCKED
     rather than hidden: the user can see the whole shape of the job from the
     first screen, and clicking a locked one says exactly what is holding it —
     which is more use than a step that simply does not respond. */
  const WIZ = ['Request', 'Services', 'Schedule', 'Review'] as const;
  const WIZ_SEC = ['sec-request', 'sec-services', 'sec-schedule', 'sec-review'];

  const wizReach = useMemo(() => {
    if (requestMissing.length) return 0;
    if (tasks.total === 0 || tasks.problems.length) return 1;
    return status && status !== 'Draft' ? 3 : 2;
  }, [requestMissing, tasks, status]);

  /** Why a step cannot be opened yet — the words the user gets, not a boolean. */
  const wizBlock = useCallback((i: number): string[] => {
    if (i >= 1 && requestMissing.length)
      return [`Request information is incomplete — ${labels(requestMissing)}.`];
    if (i >= 2 && tasks.total === 0)
      return ['Add at least one Service Task before scheduling.'];
    if (i >= 2 && tasks.problems.length) return tasks.problems;
    if (i >= 3 && (!status || status === 'Draft'))
      return ['The request has not been released. Use Save & Schedule first.'];
    return [];
  }, [requestMissing, tasks, status]);

  const goWiz = async (i: number) => {
    const why = wizBlock(i);
    if (why.length) {
      // The shared Tier-1 acknowledgement (§18.6) — a warning the user must
      // read, not a toast that scrolls away while they wonder why nothing moved.
      await alert({
        title: `${WIZ[i]} is not available yet`,
        message: why.join('\n'),
        okLabel: 'Got it',
        tone: 'warning',
      });
      return;
    }
    setWiz(i);
    const anchor = WIZ_SEC[i];
    // Review has no section yet — the step exists and stays locked until the
    // request is released, so there is simply nothing to scroll to.
    if (anchor) {
      document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const [summarising, setSummarising] = useState(false);

  /**
   * Summary — the request, every service on it and its history as ONE printable
   * document (v68 `requestPdf`).
   *
   * <p>The document is filed under the Request ID, so a request that has never
   * been saved has nothing to file it under. That refusal names the button that
   * fixes it: a Summary that silently did nothing would look like a broken
   * button rather than a request with no number yet.</p>
   */
  const doSummary = async () => {
    if (!requestId || !requestNo) {
      await alert({
        title: 'Nothing to summarise yet',
        message: 'The summary is filed under the Request ID, and this request does not have '
          + 'one until it is saved.\nPress Save Draft or Save & Schedule, then try again.',
        okLabel: 'Got it',
        tone: 'warning',
      });
      return;
    }

    setSummarising(true);
    try {
      // The header comes from the FORM, not a re-read, so the printout matches
      // what the user is looking at — including edits not yet saved.
      const outcome = await openRequestSummary(requestId, {
        requestNo,
        status,
        client:      clientLabel,
        contact:     contactLabel,
        project:     projectLabel,
        priority:    priorityLabel,
        calledOn:    form.calledOn,
        heardAbout:  form.heardAbout,
        quoteNo:     form.quoteNo,
        owner:       ownerName ?? '',
        description: form.description,
      });
      if (outcome === 'blocked') {
        notify.warning('The summary opens in a new window, and the browser blocked it. '
          + 'Allow pop-ups for this site, then press Summary again.');
      }
    } catch (e) {
      notify.error(e instanceof Error
        ? `Could not build the summary: ${e.message}`
        : 'Could not build the summary.');
    } finally {
      setSummarising(false);
    }
  };

  // Client Request detail shortcuts (CLAUDE.md standing rule 14, §18.6). Registered
  // ONCE here (the action bar renders top AND bottom, so it must not register).
  useHotkeys([
    { combo: 'mod+s', label: 'Save Draft', group: 'Client Request', order: 1, disabled: saving || loading, run: () => doSave('stay') },
    { combo: 'mod+enter', label: 'Save & Close', group: 'Client Request', order: 2, disabled: saving || loading, run: () => doSave('close') },
    { combo: 'alt+u', label: 'Save & Schedule', group: 'Client Request', order: 3, disabled: saving || releasing || released, run: () => { void doRelease(); } },
    { combo: 'alt+p', label: 'Summary', group: 'Client Request', order: 4, disabled: summarising || saving || loading, run: () => { void doSummary(); } },
    { combo: 'esc', label: 'Cancel', group: 'Client Request', order: 9, allowInInput: false, disabled: saving, run: () => { void guardedClose(); } },
  ]);

  // ── Action bar — identical set, rendered top AND bottom (BO UI §4) ────────
  const ActionBar = ({ bottom }: { bottom?: boolean }) => (
    <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
      <button className="sd-abtn primary" onClick={() => doSave('stay')} disabled={saving || loading} title={`Save Draft (${formatCombo('mod+s')})`}>
        {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save Draft
      </button>
      <button className="sd-abtn" onClick={() => doSave('close')} disabled={saving || loading} title={`Save & Close (${formatCombo('mod+enter')})`}>
        <span className="tb-i">💾</span> Save &amp; Close
      </button>
      {/* Save & Schedule is the §10.3 RELEASE GATE — a commitment to send a
          technician. It runs the SAME action as the Review and release card,
          so the two can never behave differently; when the gate is not met it
          shows what is still needed rather than sitting inert. Disabled only
          once the request has actually been released. */}
      <button className="sd-abtn" disabled={saving || releasing || released}
              title={released
                ? `Already released — status is ${status}.`
                : releaseBlockers.length
                  ? `Still needed — ${releaseBlockers.map(b => b.label).join('; ')}.`
                  : `Release this request for scheduling. (${formatCombo('alt+u')})`}
              onClick={() => void doRelease()}>
        <span className="tb-i">📅</span> Save &amp; Schedule
      </button>
      {/* Summary produces a document rather than changing anything, so it sits
          with Audit at the read-only end of the bar — not among the saves. */}
      <button className="sd-abtn" onClick={() => void doSummary()}
              disabled={summarising || saving || loading}
              title={`A summary of this request and every service on it (${formatCombo('alt+p')})`}>
        {summarising ? <BusyIcon busy icon="ti-file-text" /> : <span className="tb-i">📄</span>}
        {' '}Summary
      </button>
      {/* Audit Trail is view-only, so it is the shared quiet chip rather than an
          action button (AuditTrailButton docs / BO UI Standard v2). It is
          config-gated: hidden when audit is off for this form. */}
      {!isNew && requestId && (
        <AuditTrailButton variant="chip" formName={AUDIT_FORM_NAME} sourceId={requestId} />
      )}
      <span className="sd-abtn-sep" aria-hidden="true" />
      <button className="sd-abtn cancel" onClick={guardedClose} disabled={saving} title={`Cancel (${formatCombo('esc')})`}>
        <span className="tb-i">↩︎</span> Cancel
      </button>
    </div>
  );

  return (
    // `cr-detail` scopes this page's few style overrides (see clientRequest.css)
    // so they cannot leak into the other forms that share these classes.
    <div className="bl-page mlv-host cr-detail">
      <div className="bl-grid-area">
        <div className="sd-wrap">

          {/* Header — {BO} – {record}, breadcrumb, prev/next record arrows */}
          <div className="sd-head">
            <div>
              <h2>
                Client Request – {recordName}
                {dirty && <span className="cr-dirty" title="This request has changes that have not been saved">Unsaved changes</span>}
              </h2>
              <div className="sd-bc">
                <span>Client Request</span>
                <i className="ti ti-chevron-right" />
                <a onClick={guardedClose}>Request Registration</a>
                <i className="ti ti-chevron-right" />
                <span>{recordName}</span>
              </div>
            </div>
            <div className="sd-navarrows">
              <button className="sd-na" title="Previous record"
                      disabled={!prevId || saving || dirty}
                      onClick={() => prevId && onNavigate(prevId)}>‹</button>
              <button className="sd-na next" title="Next record"
                      disabled={!nextId || saving || dirty}
                      onClick={() => nextId && onNavigate(nextId)}>›</button>
            </div>
          </div>

          {/* The wizard (§10.4), docked in the top bar beside the form title
              (BO UI Standard §5) through the SHARED <TopbarStepper> — the same
              slot SDMS and QC Batch already use, so this form does not invent a
              second way to put a stepper up there (standing rule 8). The slot is
              PAGE-SCOPED and AppShell clears it on every page change, so no
              other form is affected by this.

              A locked step stays CLICKABLE (`isClickable` returns true for all)
              instead of rendering disabled: the user should see the whole shape
              of the job from the first screen, and clicking a locked step is how
              they learn what is holding it. A disabled button answers that
              question with silence. */}
          <TopbarStepper
            steps={WIZ.map((w, i) => ({
              id:    String(i),
              label: w,
              state: (i > wizReach ? 'todo'
                    : i < wiz     ? 'done'
                    : i === wiz   ? 'active'
                    :               'todo') as StepState,
            }))}
            onStepClick={(id) => void goWiz(Number(id))}
            isClickable={() => true}
          />

          <ActionBar />

          <div className="sd-body">
            {loading ? (
              <div className="sd-loading"><LoaderSymbol size={20} /> Loading…</div>
            ) : (
              <>
                <h3 className="sd-section-h" id="sec-request">Request Information</h3>

                {/* Label BESIDE its field, LEFT-aligned in a fixed-width label
                    column, full-width 50/50 split — the standing-rule-12 field
                    layout, same classes the Settings → Inventory Management →
                    Items detail uses (`sd-grid--side`, SettingsDetailPage.css).
                    `cr-side` carries only this form's own adjustments (label
                    column width, the notes' column, the combo/status-box
                    heights) — see clientRequest.css. */}
                <div className="sd-grid sd-grid--side cr-side">

                  {/* Request ID — minted on the first save and permanent after
                      (§4); never editable. */}
                  <div className="sd-row">
                    <label className="sd-label">Request ID</label>
                    <input className="sd-input readonly" readOnly disabled
                           value={requestNo ?? ''}
                           placeholder="Generated when saved" />
                  </div>

                  {/* Status — a read-only FIELD BOX carrying the status pill and
                      its position in the §9.1 sequence, matching the prototype.
                      It reads as a field (same border, height and greyed fill as
                      Request ID / Owner) but is not an input, because status is
                      never typed: it moves only through the §9.1 transitions. */}
                  <div className="sd-row">
                    <label className="sd-label">Status</label>
                    <div className="cr-statusbox">
                      {/* The module's OWN chip, not backlog.css's `.bl-pill`:
                          that file is never imported here, so the pill rendered
                          as bare text on a fresh load and only picked up styling
                          if the user had happened to visit a Backlog page first.
                          `.bl-pill` also has no `mute` modifier, so Draft had no
                          rule to match even when it was loaded. */}
                      <span className={`cr-chip ${
                        status === 'Completed' ? 'ok' : status === 'Draft' ? 'neutral' : 'info'}`}>
                        {status}
                      </span>
                      <span className="cr-statusstep">{statusStep}</span>
                    </div>
                  </div>

                  {/* Client — required, and the parent of the two narrowed
                      pickers below. Its ＋ (v68) is never disabled: unlike a
                      contact or a project, a client has no parent to wait for. */}
                  <div id="cr-f-client" className="sd-row">
                    <label className="sd-label">Client<span className="req">*</span></label>
                    <div className="cr-lookup">
                      <LookupCombo
                        lookup={clientLk}
                        value={clientLabel}
                        popupMatchTrigger
                        placeholder="Select"
                        className={missing(form.clientId) ? 'required-empty' : undefined}
                        onPick={onClientPick}
                        onNew={() => setQuickAdd('client')}
                        newTitle="Add a client"
                      />
                    </div>
                  </div>

                  {/* Client contact — narrowed to the chosen client (§10.1),
                      with the ＋ quick-add for when it has none on file. */}
                  <div id="cr-f-contact" className="sd-row">
                    <label className="sd-label">Client contact<span className="req">*</span></label>
                    <div className="cr-lookup">
                      <LookupCombo
                        lookup={contactLk}
                        value={contactLabel}
                        popupMatchTrigger
                        disabled={!form.clientId}
                        className={missing(form.contactId) ? 'required-empty' : undefined}
                        placeholder="Select"
                        onPick={row => set('contactId',
                          contacts.find(c => c.label === (row[0] ?? ''))?.id ?? '')}
                        onNew={() => setQuickAdd('contact')}
                        newTitle={form.clientId ? 'Add a contact for this client' : 'Select a client first'}
                      />
                    </div>
                  </div>

                  {/* Project — narrowed to the chosen client (§10.1) + ＋. */}
                  <div id="cr-f-project" className="sd-row">
                    {/* NOT mandatory (owner, 2026-10-14): a request is often taken
                        before the work has a project, so it carries no * and no
                        required tint, and the release gate below does not ask for
                        it. The server's gate was relaxed with it. */}
                    <label className="sd-label">Project</label>
                    <div className="cr-lookup">
                      <LookupCombo
                        lookup={projectLk}
                        value={projectLabel}
                        popupMatchTrigger
                        disabled={!form.clientId}
                        placeholder="Select"
                        onPick={row => set('projectId',
                          projects.find(p => p.label === (row[0] ?? ''))?.id ?? '')}
                        onNew={() => setQuickAdd('project')}
                        newTitle={form.clientId ? 'Add a project for this client' : 'Select a client first'}
                      />
                    </div>
                  </div>

                  <div id="cr-f-priority" className="sd-row">
                    <label className="sd-label">Priority<span className="req">*</span></label>
                    <select
                      className={`sd-input${missing(form.priorityId) ? ' required-empty' : ''}`}
                      value={form.priorityId}
                      onChange={e => set('priorityId', e.target.value)}
                    >
                      <option value="">— select —</option>
                      {/* A saved priority deleted in Settings is no longer offered,
                          but the request still reads as it was chosen. */}
                      {form.priorityId && !priorities.some(p => p.id === form.priorityId) && (
                        <option value={form.priorityId}>{savedPriorityName ?? form.priorityId}</option>
                      )}
                      {priorities.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select>
                  </div>

                  {/* v68 order (owner request 2026-09-15): Date of call | How did
                      you hear about us, then Quote ID | Owner. The grid fills left
                      to right, so the order of these blocks IS the layout. */}

                  {/* When the client rang — a business fact the person taking
                      the call can correct, unlike the Created stamp below. */}
                  <div className="sd-row">
                    <label className="sd-label">Date of call</label>
                    <input className="sd-input" type="date" value={form.calledOn}
                           onChange={e => set('calledOn', e.target.value)} />
                  </div>

                  <div className="sd-row">
                    <label className="sd-label">How did you hear about us</label>
                    <select className="sd-input" value={form.heardAbout}
                            onChange={e => set('heardAbout', e.target.value)}>
                      <option value="">Optional</option>
                      {heardOptions.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>

                  <div className="sd-row">
                    <label className="sd-label">Quote ID</label>
                    <input className="sd-input" value={form.quoteNo} maxLength={50}
                           placeholder="Optional"
                           onChange={e => set('quoteNo', e.target.value)} />
                  </div>

                  {/* Owner — EDITABLE since prototype v68 (2026-09-10). It used
                      to be read-only "assigned on scheduling", which described
                      the wrong person: scheduling assigns the TECHNICIAN who
                      attends. Owner is who at the desk is accountable for the
                      request, and that is known the moment the call is taken —
                      so it defaults to the signed-in user and stays editable. */}
                  <div id="cr-f-owner" className="sd-row">
                    <label className="sd-label" htmlFor="cr-owner">Owner</label>
                    <select id="cr-owner" className="sd-input"
                            value={form.ownerId}
                            onChange={e => set('ownerId', e.target.value)}>
                      <option value="">Unassigned</option>
                      {owners.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                      {/* A saved request may name someone no longer on the
                          roster. Dropping them silently would rewrite history on
                          the next save, so the stored owner is always offered. */}
                      {form.ownerId && !owners.some(o => o.id === form.ownerId) && (
                        <option value={form.ownerId}>{ownerName ?? 'Former user'}</option>
                      )}
                    </select>
                  </div>
                  <div className="cr-fnote">
                    <i className="ti ti-info-circle" />
                    <span>
                      Who at the desk is accountable for this request — not the
                      technician who attends, who is assigned in <b>Service Allocation</b>.
                    </span>
                  </div>

                  {!isNew && (
                    <div className="sd-row">
                      <label className="sd-label">Registered</label>
                      <input className="sd-input readonly" readOnly disabled
                             value={createdAt ? (fmtLocalDateTime(createdAt) || '—') : '—'} />
                    </div>
                  )}

                  {/* Description — RICH TEXT via the shared RichTextEditor (the
                      same control Project Notes and the Employee résumé use);
                      images and tables supported.

                      It sits in the LEFT column at the same width as every
                      other field (owner request 2026-09-28), not spanning both
                      as it used to: a box stretched across the whole form read
                      as a different kind of control from the fields above it.
                      Column 1 is PINNED rather than left to auto-placement,
                      because on a SAVED request the read-only "Registered" row
                      takes column 1 and an unpinned description would be placed
                      in column 2 — the right half, which is where it must not
                      be. Pinning also keeps it in the left column on a NEW
                      request, which has no Registered row. */}
                  <div id="cr-f-desc" className="sd-row" style={{ gridColumn: 1 }}>
                    <label className="sd-label">
                      Client request description<span className="req">*</span>
                    </label>
                    {/* The SIMPLE variant: block, B/I/U, lists and indent, link,
                        image, table, undo/redo. This field is prose the lab
                        writes for a technician — the legacy editor's font, size,
                        colour and align controls, and its HTML source tab, are
                        for hand-tuning a document, and each one offered is
                        another way to produce a description that does not match
                        the rest. The Employee résumé and Project Notes keep the
                        full control; nothing about them changed.

                        The tint and the footer belong to the CONTROL, so the
                        required-empty state paints the editor itself rather than
                        a wrapper around it, and the note sits inside its border
                        rather than floating below. */}
                    <RichTextEditor value={form.description}
                                    onChange={html => set('description', html)}
                                    variant="simple"
                                    invalid={triedSave && htmlIsBlank(form.description)}
                                    footerNote="Rich text · images and tables supported"
                                    showWordCount />
                  </div>

                </div>

                {/* ── Service tasks + Schedule ──────────────────────────
                    The WORK under the request. A task belongs to a SAVED
                    request (§4.3), so adding one to a NEW request mints the
                    request first via `ensureRequestId` — the user never has to
                    save the header separately before they can add a service. */}
                <ServiceTasksSection
                  ref={services}
                  requestId={requestId}
                  ensureRequestId={ensureRequestId}
                  requestNo={requestNo}
                  clientName={clientLabel}
                  projectName={projectLabel}
                  projectSite={projectSite}
                  priority={priorityLabel}
                  onSummary={setTasks} />

                {/* ── Review and release (§10.3) ──────────────────────────────
                    The gate, stated as four things that must pass rather than a
                    button that silently refuses. Each line says what is wrong,
                    not merely that something is — a checklist the user cannot
                    act on is just a slower "no".

                    It is a MIRROR of the server rule, never the rule itself: the
                    release endpoint re-checks all of it and returns this same
                    list, because a request could otherwise be released by a
                    direct call with none of it satisfied (§14b.5). */}
                <div className="cr-card" id="sec-review">
                  <div className="cr-card-h">
                    <div>
                      <h3>Review and release</h3>
                      <div className="cr-section-sub">
                        Everything below must pass before the request leaves the desk.
                      </div>
                    </div>
                  </div>

                  <CheckLine ok={!requestMissing.length} label="Request information"
                             detail={requestMissing.length
                               ? `Missing: ${labels(requestMissing)}`
                               : 'Complete'} />
                  <CheckLine ok={tasks.total > 0} label="At least one Service Task"
                             detail={tasks.total
                               ? `${tasks.total} service${tasks.total > 1 ? 's' : ''}`
                               : 'None added'} />
                  <CheckLine ok={tasks.total > 0 && !tasks.problems.length}
                             label="Every service complete and saved"
                             detail={tasks.problems.length ? tasks.problems.join(' · ')
                                   : tasks.total ? 'All services pass'
                                   : 'Nothing to check'} />
                  <CheckLine ok={released} label="Released for scheduling"
                             detail={released ? `Status is ${status}` : 'Still a draft'} />

                  {!released && (
                    <div className="cr-review-f">
                      <button className="ca-btn ca-btn-solid"
                              disabled={saving || releasing}
                              onClick={() => void doRelease()}>
                        {releasing ? <BusyIcon busy icon="ti-calendar-check" />
                                   : <span className="tb-i"><i className="ti ti-calendar-check" /></span>}
                        Save &amp; Schedule
                      </button>
                    </div>
                  )}
                </div>

                {error && (
                  <div className="sd-error"><i className="ti ti-alert-triangle" /> {error}</div>
                )}
              </>
            )}
          </div>

          <ActionBar bottom />
        </div>
      </div>

      {/* ＋ quick-add — creates the record through the EXISTING Clients
          endpoints and selects it immediately. */}
      <QuickAddDialog
        kind={quickAdd}
        clientId={form.clientId}
        clientName={clientLabel}
        // So the dialog can SAY that a client of this name already exists. It
        // never blocks: this database holds five called "City of Houston".
        clientNames={clients.map(c => c.label)}
        onClose={() => setQuickAdd(null)}
        onCreated={(id) => { if (quickAdd) void onQuickAdded(quickAdd, id); }}
      />
    </div>
  );
}
